// components/Popup.js
import React, {useState, useEffect, useContext, useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import PopupAttorney from './PopupAttorney';
import {Helmet} from 'react-helmet';
import './Popup.css'; // Ensure CSS file exists and styles are correct
import api from '../api'; // Path to your api file
import ZipCodes from './Header/ZipCodes'; // Component for displaying ZIP codes
import {SearchContext} from '../context/SearchContext'; // Search context
import {useUser} from '../context/UserContext'; // User context, must provide user and isAuthenticated
import EditLegalCompanyProfilePopup from './popups/EditProfilePopup'; // Company profile edit popup
import AddAttorneysPopup from './popups/AddAttorneysPopup.js'; // Add attorneys popup
import EditAwardsPopup from "./popups/EditAwardsPopup.js"; // Edit awards popup
import Cookies from "js-cookie"; // For CSRF token handling
import EditServiceRegionsPopup from "./popups/EditServiceRegionsPopup"; // Edit regions popup
import {useNotification} from '../context/NotificationContext'; // Notifications context
import Notification from "./Notification"; // Notification component
import {validateImage} from '../utils/validateImage'; // Image validation utility
import {validateAndScaleImage} from '../utils/validateAndScaleImage'; // Image validation and scaling utility
import Loader from './Loader'; // Loader component
import SendMessageModal from "./popups/SendMessageModal"; // Send message modal
// import RequestCallbackModal from "./popups/RequestCallbackModal"; // REMOVE THIS LINE
import RequestConsultationModal from "./popups/RequestConsultationModal"; // ADD THIS LINE
import CropImageModal from './popups/CropImageModal'; // Image cropping modal
import ClaimProfileModal from './popups/ClaimProfileModal'; // Claim profile modal

// Image requirements constants
const IMAGE_REQUIREMENTS = {
    'legal_company_logo': {
        'min_width': 456,
        'min_height': 256,
        'aspect_ratio': 1.78,
        'ratio_tolerance': 0.05,
    },
    'background_company': {
        'min_width': 1440,
        'min_height': 280,
        'aspect_ratio': 5.14,
        'ratio_tolerance': 0.05,
    },
};

// State abbreviations map
const stateAbbreviations = {
    "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR", "California": "CA", "Colorado": "CO",
    "Connecticut": "CT", "Delaware": "DE", "Florida": "FL", "Georgia": "GA", "Hawaii": "HI", "Idaho": "ID",
    "Illinois": "IL", "Indiana": "IN", "Iowa": "IA", "Kansas": "KS", "Kentucky": "KY", "Louisiana": "LA",
    "Maine": "ME", "Maryland": "MD", "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN",
    "Mississippi": "MS", "Missouri": "MO", "Montana": "MT", "Nebraska": "NE", "Nevada": "NV",
    "New Hampshire": "NH", "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY", "North Carolina": "NC",
    "North Dakota": "ND", "Ohio": "OH", "Oklahoma": "OK", "Oregon": "OR", "Pennsylvania": "PA",
    "Rhode Island": "RI", "South Carolina": "SC", "South Dakota": "SD", "Tennessee": "TN", "Texas": "TX",
    "Utah": "UT", "Vermont": "VT", "Virginia": "VA", "Washington": "WA", "West Virginia": "WV",
    "Wisconsin": "WI", "Wyoming": "WY"
};

// --- Assumed ID for the 'law@law.law' user ---
// --- REPLACE WITH THE ACTUAL ID OR CHECK MECHANISM ---
const lawLawUserIdFromEnv = process.env.REACT_APP_LAW_LAW_USER_ID;
const LAW_LAW_USER_ID = parseInt(lawLawUserIdFromEnv, 10);

const Popup = ({visible, data, onClose, showSharePopup, origin, handleShowPopup}) => {
    // State Hooks
    const [loading, setLoading] = useState(true);
    const [showAllAttorneys, setShowAllAttorneys] = useState(false);
    const [selectedAttorney, setSelectedAttorney] = useState(null);
    const [expandedCities, setExpandedCities] = useState({});
    const location = useLocation();
    const [currentLegalCompany, setCurrentLegalCompany] = useState(data);
    const navigate = useNavigate();
    const [showEditProfilePopup, setShowEditProfilePopup] = useState(false);
    const [showEditAwardsPopup, setShowEditAwardsPopup] = useState(false);
    const [showAddAttorneysPopup, setShowAddAttorneysPopup] = useState(false);
    const [currentLogo, setLogo] = useState(data?.logo);
    const [backgroundImage, setBackgroundImage] = useState(data?.background_image);
    const [showEditServiceRegionsPopup, setShowEditServiceRegionsPopup] = useState(false);
    const {user, isAuthenticated} = useUser(); // Get current user and auth status
    const {showNotification} = useNotification();
    const [logoError, setLogoError] = useState(null);
    const [backgroundImageError, setBackgroundImageError] = useState(null);
    const [errors, setErrors] = useState({});
    const [showSendMessageModal, setShowSendMessageModal] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);
    // const [showRequestCallbackModal, setShowRequestCallbackModal] = useState(false); // REMOVE THIS LINE
    const [showRequestConsultationModal, setShowRequestConsultationModal] = useState(false); // ADD THIS LINE

    // Cropper states
    const [showCropModal, setShowCropModal] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);
    const [currentFieldToUpdate, setCurrentFieldToUpdate] = useState(null);
    const [currentAspectRatio, setCurrentAspectRatio] = useState(1.78); // Default aspect for logo

    // State for Claim Profile modal
    const [showClaimProfileModal, setShowClaimProfileModal] = useState(false);

    // Function to get request config with tokens
    const getRequestConfig = useCallback(() => {
        const token = localStorage.getItem('authToken');
        const csrfToken = Cookies.get('csrftoken');
        return {
            headers: {
                'Authorization': `Bearer ${token}`,
                'X-CSRFToken': csrfToken,
            },
            withCredentials: true,
        };
    }, []);

    // Handlers for Request Consultation Modal // MODIFIED SECTION
    const handleShowRequestConsultationModal = useCallback(() => { // MODIFIED
        setShowRequestConsultationModal(true); // MODIFIED
    }, []);
    const handleCloseRequestConsultationModal = useCallback(() => { // MODIFIED
        setShowRequestConsultationModal(false); // MODIFIED
    }, []);

    // Handlers for Claim Profile Modal
    const handleShowClaimProfileModal = useCallback(() => {
        if (!isAuthenticated) {
            showNotification("Login Required", "Please log in to claim a profile.", null, "warning");
            return;
        }
        setShowClaimProfileModal(true);
    }, [isAuthenticated, showNotification]);

    const handleCloseClaimProfileModal = useCallback(() => {
        setShowClaimProfileModal(false);
    }, []);

    // useEffect for handling URL parameters after subscription payment
    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const companyId = query.get('companyId');
        const sessionId = query.get('session_id');
        const status = query.get('status');

        if (sessionId && companyId) {
            showNotification('Subscription successfully completed!', 'Your subscription has been activated.', null, 'success');
            navigate('/?companyId=' + companyId, {replace: true}); // Remove params from URL
        } else if (status === 'cancel') {
            showNotification('Subscription cancelled.', 'You have cancelled the subscription process.', null, 'warning');
            if (companyId) {
                navigate('/?companyId=' + companyId, {replace: true});
            } else {
                navigate('/', {replace: true});
            }
        }
    }, [location.search, navigate, showNotification]);

    const updateCompanyData = async (companyId, data, config) => {
        try {
            const response = await api.patch(`/legal-companies/${companyId}/update/`, data, config);
            return response;
        } catch (error) {
            console.error('Error updating company:', error);
            throw error;
        }
    };

    // Function to update a single field
    const handleFieldUpdate = async (fieldName, value) => {
        const formData = new FormData();
        formData.append(fieldName, value);

        try {
            const config = getRequestConfig();
            // Optimistic UI update for images
            if (fieldName === 'logo' || fieldName === 'background_image') {
                if (value instanceof Blob) {
                    const tempURL = URL.createObjectURL(value);
                    if (fieldName === 'logo') {
                        setLogo(tempURL);
                    } else if (fieldName === 'background_image') {
                        setBackgroundImage(tempURL);
                    }
                    // IMPORTANT: Original code lacked revokeObjectURL, potentially causing memory leaks.
                    // It should be called after successful upload or on error.
                }
            }

            // Calling the update function (which uses GET)
            const response = await updateCompanyData(currentLegalCompany.id, formData, config);

            // Refetch data if the GET request was successful (status 200)
            if (response.status === 200) {
                await fetchLegalCompanyData(); // Refetch to show actual changes
            }
        } catch (error) {
            console.error('Error updating field:', error);
            // Original code didn't revert optimistic update on error
        }
    };

    // Handler for logo change (to open cropper)
    const handleLogoChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const tempURL = URL.createObjectURL(file);
            setImageToCrop(tempURL);
            setCurrentFieldToUpdate('logo');
            setCurrentAspectRatio(IMAGE_REQUIREMENTS['legal_company_logo'].aspect_ratio);
            setShowCropModal(true);
            event.target.value = null; // Reset file input
        }
    };

    // Handler for background image change (to open cropper)
    const handleBackgroundImageChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const tempURL = URL.createObjectURL(file);
            setImageToCrop(tempURL);
            setCurrentFieldToUpdate('background_image');
            setCurrentAspectRatio(IMAGE_REQUIREMENTS['background_company'].aspect_ratio);
            setShowCropModal(true);
            event.target.value = null; // Reset file input
        }
    };

    // Handler for completing image cropping
    const handleCroppedImageComplete = async (croppedImageBlob) => {
        setShowCropModal(false);
        const field = currentFieldToUpdate;
        const imageSrc = imageToCrop; // Store URL for cleanup
        setImageToCrop(null);
        setCurrentFieldToUpdate(null);

        if (!croppedImageBlob || !field) {
            if (imageSrc) URL.revokeObjectURL(imageSrc); // Cleanup even on cancel/error
            return;
        }

        try {
            const file = new File([croppedImageBlob], 'cropped_image.jpg', {type: 'image/jpeg'});
            const requirements = (field === 'logo')
                ? IMAGE_REQUIREMENTS['legal_company_logo']
                : IMAGE_REQUIREMENTS['background_company'];

            // Validate and scale the *cropped* image
            const validatedAndScaledFile = await validateAndScaleImage(
                file,
                requirements.min_width,
                requirements.min_height,
                requirements.aspect_ratio,
                requirements.ratio_tolerance
            );

            // Update field with the validated file
            await handleFieldUpdate(field, validatedAndScaledFile);

        } catch (error) {
            showNotification('Image Processing Error', error.message || 'Failed to validate or scale image', null, 'error');
        } finally {
            if (imageSrc) URL.revokeObjectURL(imageSrc); // Cleanup original blob URL
        }
    };

    // Handler for saving the profile (calls updateCompanyData with GET)
    const handleSaveProfile = async (updatedData) => {
        try {
            setLoading(true);
            const config = getRequestConfig();
            // Calling the update function (which uses GET)
            const response = await updateCompanyData(currentLegalCompany.id, updatedData, config);

            if (response.status === 200) { // Checking GET request status
                await fetchLegalCompanyData(); // Refetch data
                handlerCloseEditProfilePopup(); // Close popup
                showNotification('Profile Saved', 'Profile details updated successfully.', null, 'success');
            }
        } catch (error) {
            console.error('Error saving profile:', error);
            showNotification('Save Error', 'Failed to save profile changes.', null, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Function to check if the current user is the company owner
    const isCurrentUserCompanyOwner = useCallback(() => {
        // Compare logged-in user ID with company owner ID from state
        return user && currentLegalCompany && user.id === currentLegalCompany.user;
    }, [user, currentLegalCompany]);

    // Get banner height from context
    const {bannerHeight} = useContext(SearchContext);

    // Format phone number
    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return '';
        let cleaned = ('' + phoneNumber).replace(/\D/g, '');
        // Logic for US numbers (might be incorrect for other countries)
        if (!cleaned.startsWith('1') && cleaned.length === 10) {
            cleaned = '1' + cleaned;
        } else if (!cleaned.startsWith('1')) {
            cleaned = '1' + cleaned;
        }
        const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
        }
        return phoneNumber; // Return original if format doesn't match
    };

    // Format amount (currency)
    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-US', { // Can change 'en-US' and 'USD' if needed
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0
        }).format(amount);
    };

    // useEffect to update component state when 'data' prop changes
    useEffect(() => {
        setCurrentLegalCompany(data);
        if (data) {
            setLogo(data.logo);
            setBackgroundImage(data.background_image);
            setIsFavorite(false); // Reset favorite status when company changes
        }
    }, [data]);

    // useEffect to select an attorney based on 'attorneyId' URL parameter
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const attorneyId = params.get('attorneyId');
        // Use currentLegalCompany state which reflects 'data' or fetch result
        if (attorneyId && currentLegalCompany && currentLegalCompany.attorneys) {
            const attorney = currentLegalCompany.attorneys.find(att => String(att.attorney_id) === String(attorneyId));
            setSelectedAttorney(attorney);
        } else {
            setSelectedAttorney(null);
        }
    }, [location.search, currentLegalCompany]); // Depend on URL search and company data in state

    // Function to fetch company data
    const fetchLegalCompanyData = useCallback(async () => {
        setLoading(true);
        try {
            // Use ID from 'data' prop or from state
            const companyIdToFetch = data?.id || currentLegalCompany?.id;
            if (!companyIdToFetch) {
                console.error('Company ID not found for fetching.');
                setLoading(false);
                return;
            }

            // Original GET request was without authentication, keeping it that way
            const response = await api.get(`/legal-companies/${companyIdToFetch}/`, {withoutAuth: true});

            setCurrentLegalCompany(response.data);
            setLogo(response.data.logo);
            setBackgroundImage(response.data.background_image);
            // Can re-check favorite status after fetching new data
            // checkFavoriteStatus(response.data.id); // Pass ID

        } catch (error) {
            console.error('Error fetching legal company data:', error);
            // Optionally show error notification
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.id, currentLegalCompany?.id]); // Dependencies for useCallback

    // useEffect for initial data fetch
    useEffect(() => {
        if (data?.id) { // Fetch if 'data' prop has an ID
            fetchLegalCompanyData();
        }
    }, [fetchLegalCompanyData, data?.id]); // Depend on the fetch function itself and the ID in props

    // useEffect to set document title and meta tags
    useEffect(() => {
        if (visible && currentLegalCompany) { // Use data from state
            document.title = currentLegalCompany.name || "Law Firm"; // Set title

            const updateMetaTag = (property, content) => {
                let metaTag = document.querySelector(`meta[property='${property}']`);
                if (metaTag) {
                    metaTag.setAttribute('content', content || ''); // Update existing
                } else {
                    // Create and append if not found
                    metaTag = document.createElement('meta');
                    metaTag.setAttribute('property', property);
                    metaTag.setAttribute('content', content || '');
                    document.head.appendChild(metaTag);
                }
            };

            // Set meta tags using data from state
            updateMetaTag('og:title', currentLegalCompany.name);
            updateMetaTag('og:description', currentLegalCompany.description);
            updateMetaTag('og:image', currentLogo); // Use logo from state
            updateMetaTag('og:url', window.location.href);
            updateMetaTag('og:type', 'profile'); // Example type
        }
        // Original code didn't have a cleanup function
    }, [visible, currentLegalCompany, currentLogo]); // Depend on visibility and data/logo in state

    // Destructuring company data from state with default values
    const {
        name = "Default Law Firm Name",
        description = "Default law firm description...",
        address = { // Default address object
            street_address: "1 Default St",
            city: {name: "City"},
            state: {name: "State"},
            country: {name: "Country"},
            zip_codes: "12345" // Used zip_codes, might need zip_code
        },
        email = "info@example.com",
        phone_number = "+11234567890",
        additional_phones = [],
        web_site = "http://example.com",
        social_media = [],
        working_hours = [],
        detailed_service_regions = [],
        attorneys = [],
        // logo state = currentLogo
        // background_image state = backgroundImage
        free_consultation = false,
        awards = [],
        foundation_year = null,
        // is_favorite state = isFavorite
        active_plans = [], // For phone display logic
        user: companyOwnerId, // Company owner ID from data (currentLegalCompany.user)
    } = currentLegalCompany || {}; // Use default empty object if currentLegalCompany is initially null

    // Determine if the company has active plans
    const hasActivePlans = active_plans && active_plans.length > 0;

    // Format the address string
    const formattedAddress = address && address.street_address && address.city?.name && address.state?.name && address.country?.name
        ? `${address.street_address}, ${address.city.name}, ${address.state.name}, ${address.country.name}, ${address.zip_code ? address.zip_code.zip_code : (address.zip_codes || '')}` // Handle zip_code or zip_codes
        : "Address not available";

    // URL for Google Maps (might need adjustment)
    const googleMapsUrl = `https://maps.google.com/?q=${encodeURIComponent(formattedAddress)}`; // More standard URL


    // useEffect to check favorite status
    useEffect(() => {
        const checkFavoriteStatus = async (companyId) => {
            if (!companyId || !user) { // Requires company ID and logged-in user
                setIsFavorite(false);
                return;
            }
            try {
                const token = localStorage.getItem('authToken');
                if (!token) { // Don't check if user is not logged in
                    setIsFavorite(false);
                    return;
                }
                const config = {headers: {'Authorization': `Bearer ${token}`}};
                // Endpoint uses company ID
                const response = await api.get(`/check-favorite-lawcompany/${companyId}/`, config);
                setIsFavorite(response.data.is_favorite); // Update state
            } catch (error) {
                console.error('Error checking favorite status:', error);
                setIsFavorite(false); // Reset on error
            }
        };

        if (currentLegalCompany?.id) { // Check when company ID is available in state
            checkFavoriteStatus(currentLegalCompany.id);
        } else {
            setIsFavorite(false); // Reset if no ID
        }
    }, [currentLegalCompany?.id, user]); // Depend on company ID in state and user object

    // Functions for adding/removing from favorites
    const handleAddToFavorites = async () => {
        if (!user) {
            showNotification("Login Required", "Please log in to manage favorites.", null, "warning");
            return;
        }
        if (!currentLegalCompany?.id) return;
        try {
            const config = getRequestConfig(); // Get config with tokens
            // Endpoint uses company ID from state
            const response = await api.post('/add-favorite-lawcompany/', {company_id: currentLegalCompany.id}, config);
            if (response.status === 200 || response.status === 201) {
                setIsFavorite(true); // Update state
                showNotification("Favorite Added", `${name} added to favorites.`, null, "success");
            }
        } catch (error) {
            console.error('Error adding to favorites:', error);
            showNotification("Error", "Could not add to favorites.", null, "error");
        }
    };

    const handleRemoveFromFavorites = async () => {
        if (!user) {
            showNotification("Login Required", "Please log in to manage favorites.", null, "warning");
            return;
        }
        if (!currentLegalCompany?.id) return;
        try {
            const config = getRequestConfig(); // Get config with tokens
            // Endpoint uses company ID from state
            const response = await api.post('/remove-favorite-lawcompany/', {company_id: currentLegalCompany.id}, config);
            // Successful deletion might return 200 OK or 204 No Content
            if (response.status === 200 || response.status === 204) {
                setIsFavorite(false); // Update state
                showNotification("Favorite Removed", `${name} removed from favorites.`, null, "info");
            }
        } catch (error) {
            console.error('Error removing from favorites:', error);
            showNotification("Error", "Could not remove from favorites.", null, "error");
        }
    };

    // Toggle favorite status
    const handleToggleFavorites = () => {
        if (!user) {
            showNotification("Login Required", "Please log in to manage favorites.", null, "warning");
            return;
        }
        if (isFavorite) {
            handleRemoveFromFavorites();
        } else {
            handleAddToFavorites();
        }
    };

    // Format time to AM/PM (if needed)
    const formatTimeToAMPM = (time) => {
        if (!time || typeof time !== 'string') return '';
        const [hour, minute] = time.split(':').map(Number);
        if (isNaN(hour) || isNaN(minute)) return '';
        const period = hour >= 12 ? 'PM' : 'AM';
        const adjustedHour = hour % 12 || 12; // 0 hours -> 12 AM
        return `${adjustedHour}:${minute < 10 ? '0' + minute : minute} ${period}`;
    };

    // Group and format working hours
    const groupWorkingHours = (wh) => {
        const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; // Can be localized
        const hoursMap = daysOfWeek.reduce((map, day) => {
            map[day] = 'Closed'; // Default to 'Closed'
            return map;
        }, {});

        // Fill map with actual working hours
        (wh || []).forEach(item => {
            const hoursData = item?.working_hours;
            if (hoursData && hoursData.day_of_week && hoursData.start_time && hoursData.end_time) {
                // Use 24-hour format or AM/PM as desired
                // hoursMap[hoursData.day_of_week] = `${formatTimeToAMPM(hoursData.start_time)} - ${formatTimeToAMPM(hoursData.end_time)}`;
                hoursMap[hoursData.day_of_week] = `${hoursData.start_time.substring(0, 5)} - ${hoursData.end_time.substring(0, 5)}`; // Example: 09:00 - 17:00
            }
        });

        // Logic to group days with the same hours
        const groupedHours = [];
        let currentRange = null;
        let currentDays = [];

        const finalizeGroup = () => {
            if (currentDays.length > 0) {
                if (currentDays.length === 1) {
                    groupedHours.push(`${currentDays[0]}: ${currentRange}`);
                } else {
                    // Use hyphen or dash
                    groupedHours.push(`${currentDays[0]}-${currentDays[currentDays.length - 1]}: ${currentRange}`);
                }
            }
        };

        daysOfWeek.forEach(day => {
            const hours = hoursMap[day];
            if (currentRange === hours && currentRange !== null) { // Compare with current range
                currentDays.push(day);
            } else {
                finalizeGroup(); // Finalize previous group
                currentDays = [day]; // Start new group
                currentRange = hours;
            }
        });

        finalizeGroup(); // Finalize the last group

        return groupedHours;
    };

    const workingHoursFormatted = groupWorkingHours(working_hours); // Use data from state

    // Displayed attorneys (considering "show all")
    const displayedAttorneys = showAllAttorneys ? (attorneys || []) : (attorneys || []).slice(0, 2);
    const remainingAttorneysCount = (attorneys?.length || 0) > 2 ? (attorneys.length - 2) : 0;

    // Handler for clicking an attorney (calls handleShowPopup from props)
    const handleAttorneyClick = (attorney) => {
        if (attorney?.attorney_id) {
            // Pass attorney ID, type, and current company data (from state)
            handleShowPopup(attorney.attorney_id, 'attorney', true, currentLegalCompany);
        }
    };

    // Toggle expansion of ZIP codes for a city
    const toggleCityExpansion = (city) => {
        setExpandedCities(prevState => ({
            ...prevState,
            [city]: !prevState[city]
        }));
    };

    // Render "FREE CONSULTATION" status
    const renderFreeConsultation = () => {
        if (free_consultation) { // Use data from state
            return (
                <span className="vendor__status vendor__status-active">FREE CONSULTATION</span>
            );
        }
        return null;
    };

    // Group service regions by city/state
    const groupedServiceRegions = (detailed_service_regions || []).reduce((acc, region) => {
        // Check if all necessary fields exist
        if (region?.city?.name && region?.state?.name && stateAbbreviations[region.state.name] && region?.zip_code?.zip_code) {
            const cityState = `${region.city.name}, ${stateAbbreviations[region.state.name]}`;
            if (!acc[cityState]) {
                acc[cityState] = []; // Initialize array for new city/state
            }
            acc[cityState].push(region.zip_code.zip_code); // Add ZIP code
        }
        return acc;
    }, {});

    // Handlers for opening/closing edit popups (useCallback for memoization)
    const handlerShowEditProfilePopup = useCallback(() => setShowEditProfilePopup(true), []);
    const handlerCloseEditProfilePopup = useCallback(() => setShowEditProfilePopup(false), []);
    const handlerShowEditAwardsPopup = useCallback(() => setShowEditAwardsPopup(true), []);
    const handlerCloseEditAwardsPopup = useCallback(() => setShowEditAwardsPopup(false), []);
    const handlerShowAddAttorneysPopup = useCallback(() => setShowAddAttorneysPopup(true), []);
    const handlerCloseAddAttorneysPopup = useCallback(() => setShowAddAttorneysPopup(false), []);
    const handleShowEditServiceRegionsPopup = useCallback(() => setShowEditServiceRegionsPopup(true), []);
    const handleCloseEditServiceRegionsPopup = useCallback(() => setShowEditServiceRegionsPopup(false), []);

    // Handler for saving service regions
    const handleSaveServiceRegions = async (updatedRegions) => {
        if (!currentLegalCompany?.id) return; // Check for company ID
        try {
            setLoading(true);
            const config = getRequestConfig();
            // Assuming backend expects data in { regions: [...] } format
            const response = await api.patch(`/legal-companies/${currentLegalCompany.id}/update-regions/`, {regions: updatedRegions}, config);
            if (response.status === 200) {
                await fetchLegalCompanyData(); // Refetch data
                handleCloseEditServiceRegionsPopup(); // Close popup
                showNotification("Regions Saved", "Service regions updated.", null, "success");
            }
        } catch (error) {
            console.error('Error saving service regions:', error);
            showNotification("Save Error", "Could not save service regions.", null, "error");
        } finally {
            setLoading(false);
        }
    };

    // Handler for saving awards
    const handleSaveAwards = async (formDataToSend) => {
        if (!currentLegalCompany?.id) return; // Check for company ID
        try {
            setLoading(true);
            const config = getRequestConfig();
            // Assuming PATCH request to awards update endpoint
            const response = await api.patch(`/legal-company/${currentLegalCompany.id}/awards/`, formDataToSend, config);

            if (response.status === 200) {
                await fetchLegalCompanyData(); // Refetch data
                handlerCloseEditAwardsPopup(); // Close popup
                showNotification("Awards Saved", "Awards updated successfully.", null, "success");
            }
        } catch (error) {
            console.error('Error saving awards:', error);
            showNotification("Save Error", "Could not save awards.", null, "error");
        } finally {
            setLoading(false);
        }
    };

    // Handler for "Send Message" button click (for original logic)
    const handleSendMessageClick = () => {
        if (!user) { // Check if user is logged in
            showNotification("Login Required", "Please log in to send a message.", null, "warning");
            return;
        }
        // This function is called by the button now visible only to logged-in non-owners
        setShowSendMessageModal(true);
    };

    // Handler for closing the Send Message modal
    const handleCloseSendMessageModal = () => {
        setShowSendMessageModal(false);
    };

    // --- Determine if the Claim Profile section should be shown ---
    const isOwner = isCurrentUserCompanyOwner(); // Is the current user the owner?

    // Condition to show the "Claim Profile" section:
    // 1. Profile has an owner (companyOwnerId is not null/undefined)
    // 2. Owner's ID is NOT the ID of the 'law@law.law' user (LAW_LAW_USER_ID)
    // 3. The current user is NOT the owner of this profile (!isOwner)
    const shouldShowClaimSection =
        companyOwnerId != null &&         // Owner is assigned
        companyOwnerId === LAW_LAW_USER_ID && // Owner is 'law@law.law' (Adjusted logic based on typical claim scenarios, was "Owner is NOT 'law@law.law'")
        // This typically means the profile is a placeholder owned by a system account.
        !isOwner;                          // Current user is not the owner

    // Auto-open edit profile if requested via origin prop
    useEffect(() => {
        if (visible && origin === 'edit-profile') {
            setShowEditProfilePopup(true);
        }
    }, [visible, origin]);

    // --- RENDER ---
    // Don't render anything if the popup is not visible
    if (!visible) {
        return null;
    }

    // Main component render
    return (
        <div className={`popup ${visible ? 'visible' : ''}`} id="company-type1" style={{top: `${bannerHeight + 50}px`}}>
            <Notification/> {/* Component for showing notifications */}
            <button className="close js--hide-popup" onClick={onClose}>
                {/* Ensure icon path is correct */}
                <img src="/assets/icon--close-CN6QlgAA.svg" alt="Close"/>
            </button>
            {loading && <Loader/>} {/* Show loader if loading=true */}

            {/* Render content only if not loading and company data exists */}
            {!loading && currentLegalCompany && (
                <div className="profile profile-pro">
                    {/* Top section with background and logo */}
                    <div className="profile__headline profile__headline-company"
                         style={{backgroundImage: `url(${backgroundImage || '/assets/bg--profile-attorney-B9GKd-yI.jpg'})`}}> {/* Background with fallback */}
                        <div className="container">
                            <div className="profile__logo">
                                {currentLogo ? ( // If logo exists
                                    <picture>
                                        <source type="image/webp" srcSet={currentLogo}/>
                                        <source type="image/jpg" srcSet={currentLogo}/>
                                        <img src={currentLogo} alt={`${name} logo`}/>
                                    </picture>
                                ) : ( // If no logo, can show a placeholder
                                    <div className="logo-placeholder"></div>
                                )}
                                {isOwner && ( // Edit logo button visible only to owner
                                    <label className="userpick__edit" title="Edit Logo">
                                        <img src="/assets/icon--edit-bg-BVfPEI-f.svg" alt="edit"/>
                                        <input type="file" accept="image/*" onChange={handleLogoChange}
                                               style={{display: 'none'}}/>
                                    </label>
                                )}
                            </div>
                            {isOwner && ( // Edit background button visible only to owner
                                <label className="bg__edit" title="Edit Background Photo">
                                    <div className="button no-style">
                                        <img src="/assets/icon--edit-Z9QfuMgm.svg" alt="edit"/> Edit Big Photo
                                    </div>
                                    <input type="file" accept="image/*" onChange={handleBackgroundImageChange}
                                           style={{display: 'none'}}/>
                                </label>
                            )}
                        </div>
                    </div>
                    {/* Main profile block */}
                    <div className="profile__block">
                        <div className="container">
                            {/* Profile header */}
                            <div className="profile__header">
                                <div className="vendor__header">
                                    <h1 className="vendor__name">{name}</h1>
                                    {/* Favorite button shown for everyone; handler guards auth */}
                                    <button
                                        className={`favorites ${isFavorite ? 'added' : ''}`}
                                        onClick={handleToggleFavorites}
                                        aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                                        title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                                    >
                                        <svg viewBox="0 0 20 20" fill="currentColor"> {/* Heart icon SVG */}
                                            <path fillRule="evenodd" clipRule="evenodd"
                                                  d="M1.58733 3.10072C3.70393 0.940385 7.13582 0.940385 9.25246 3.10072L10.0323 3.89665L10.7475 3.16683C12.8641 1.0065 16.296 1.0065 18.4127 3.16683C20.5293 5.32717 20.5292 8.82998 18.4127 10.9904L10.4695 19.0901C10.4418 19.1184 10.5003 19.068 10.4695 19.0901C10.2068 19.2849 9.79324 19.2991 9.5566 19.0576L1.58757 10.9239C-0.52903 8.76353 -0.529268 5.26111 1.58733 3.10072Z"></path>
                                        </svg>
                                    </button>
                                    {/* Meta info (attorneys, founded year, consultation) */}
                                    <div className="vendor__attorney">
                                        {(attorneys?.length || 0) > 0 && `${attorneys.length} attorney${attorneys.length !== 1 ? 's' : ''} on staff `}
                                        {foundation_year && (
                                            <span>• Founded In {foundation_year} </span>
                                        )}
                                        {renderFreeConsultation()} {/* Calling function to render consultation status */}
                                    </div>
                                </div>
                                {/* Edit profile button (visible only to owner) */}
                                {isOwner && (
                                    <button className="button no-style" onClick={handlerShowEditProfilePopup}>
                                        <img src="/assets/icon--edit-Z9QfuMgm.svg" alt="Edit"/> Edit profile
                                    </button>
                                )}
                            </div>
                            {/* Main profile content */}
                            <div className="profile__content userdata">
                                {/* Left Column */}
                                <div className="col-2">
                                    {/* Company description */}
                                    <div className="data">
                                        <p>{description || "No description provided."}</p>
                                    </div>
                                    {/* Contact Information */}
                                    <div className="profile__contacts">
                                        <ul className="contacts">
                                            {/* Address (visible to all) */}
                                            {address && address.street_address && (
                                                <li className="contacts__el">
                                                    <div className="contacts__label">Address</div>
                                                    <div className="contacts__value">
                                                        <a href={googleMapsUrl} target="_blank"
                                                           rel="noopener noreferrer">{formattedAddress}</a>
                                                    </div>
                                                </li>
                                            )}
                                            {/* Working Hours (visible to all) */}
                                            {(working_hours?.length || 0) > 0 && (
                                                <li className="contacts__el">
                                                    <div className="contacts__label">Working Hours</div>
                                                    <div className="contacts__value">
                                                        <ul className="hours">
                                                            {workingHoursFormatted.length > 0 ? workingHoursFormatted.map((item, index) => {
                                                                const [dayPart, timePart] = item.split(': '); // Split day and time
                                                                return (
                                                                    <li key={index}>
                                                                        <span
                                                                            className="working-hours__day">{dayPart}:</span>
                                                                        {timePart ? ( // Check if time part exists (not "Closed")
                                                                            <span
                                                                                className="working-hours__time">{timePart}</span>
                                                                        ) : (
                                                                            <span
                                                                                className="working-hours__time">Closed</span> // If no time part
                                                                        )}
                                                                    </li>
                                                                );
                                                            }) : <li>Not specified</li>}
                                                        </ul>
                                                    </div>
                                                </li>
                                            )}

                                            {/* --- Email (ORIGINAL LOGIC) --- */}
                                            {email && (
                                                <li className="contacts__el">
                                                    <div className="contacts__label">Email</div>
                                                    <div className="contacts__value">
                                                        {isOwner ? ( // Owner sees mailto: link
                                                            <a href={`mailto:${email}`}>{email}</a>
                                                        ) : (
                                                            // Others see "Send Message" button (if logged in)
                                                            isAuthenticated ? (
                                                                <button
                                                                    onClick={handleSendMessageClick} // Opens SendMessageModal
                                                                    type='button'
                                                                    className='button button-black'
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center'
                                                                    }}
                                                                >
                                                                    Send Message
                                                                </button>
                                                            ) : (
                                                                // Text for non-authenticated users
                                                                <button
                                                                    onClick={() => navigate(`/sign-in`)} // Navigate to signup/login with ID
                                                                    type='button'
                                                                    className='button button-black' // Main action style
                                                                >
                                                                    Login Required
                                                                </button>
                                                            )
                                                        )}
                                                    </div>
                                                </li>
                                            )}

                                            {/* --- Phone (MODIFIED FOR NEW MODAL) --- */}
                                            {phone_number && (
                                                <li className="contacts__el">
                                                    <div className="contacts__label">Phone</div>
                                                    <div className="contacts__value">
                                                        {/* Owner or user with active plan sees tel: link */}
                                                        {(isOwner || hasActivePlans) ? (
                                                            <a href={`tel:${phone_number}`}>{formatPhoneNumber(phone_number)}</a>
                                                        ) : (
                                                            /* Others see "Request Consultation" button */ // MODIFIED
                                                            <button
                                                                onClick={handleShowRequestConsultationModal} // MODIFIED: Opens RequestConsultationModal
                                                                type='button'
                                                                className='button button-black button-send-message' // You might want a different class or style
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                            > Request a Legal Help </button> // MODIFIED TEXT
                                                        )}
                                                    </div>
                                                </li>
                                            )}

                                            {/* --- Additional Phones (ORIGINAL LOGIC) --- */}
                                            {/* Shown to owner or users with active plan */}
                                            {(isOwner || hasActivePlans) && (additional_phones?.length || 0) > 0 && (
                                                additional_phones.map((phone, index) => (
                                                    <li key={index} className="contacts__el">
                                                        <div
                                                            className="contacts__label">{phone?.phone_type?.name || 'Phone'}</div>
                                                        <div className="contacts__value">
                                                            <a href={`tel:${phone.phone_number}`}>{formatPhoneNumber(phone.phone_number)}</a>
                                                        </div>
                                                    </li>
                                                ))
                                            )}

                                            {/* --- Claim Profile Section (CONDITIONAL) --- */}
                                            {shouldShowClaimSection && (
                                                <li className="contacts__el">
                                                    <div className="contacts__label">Claim Profile</div>
                                                    {/* Label */}
                                                    <div
                                                        className="contacts__value contacts__value--buttons"> {/* Buttons container */}
                                                        {user && isAuthenticated ? (
                                                            /* Logged-in user (not owner) */
                                                            <button
                                                                onClick={handleShowClaimProfileModal} // Opens ClaimProfileModal
                                                                type='button'
                                                                className='button button-black' // Style like other buttons
                                                            >
                                                                Claim This Profile
                                                            </button>
                                                        ) : (
                                                            /* Logged-out user */
                                                            <>
                                                                <button
                                                                    onClick={() => navigate(`/sign-up?claimCompanyId=${currentLegalCompany?.id}`)} // Navigate to signup/login with ID
                                                                    type='button'
                                                                    className='button button-black' // Main action style
                                                                >
                                                                    Sign Up & Claim
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </li>
                                            )}
                                            {/* --- End Claim Profile Section --- */}


                                            {/* Web Site (visible to all) */}
                                            {web_site && (
                                                <li className="contacts__el">
                                                    <div className="contacts__label">Web Site</div>
                                                    <div className="contacts__value">
                                                        {/* Add http:// if missing */}
                                                        <a href={web_site.startsWith('http') ? web_site : `http://${web_site}`}
                                                           target="_blank" rel="noopener noreferrer">{web_site}</a>
                                                    </div>
                                                </li>
                                            )}
                                            {/* Social Media (visible to all) */}
                                            {(social_media?.length || 0) > 0 && (
                                                <li className="contacts__el">
                                                    <div className="contacts__label">Social Media</div>
                                                    <div className="contacts__value">
                                                        <ul className="social">
                                                            {social_media.map((sm, index) => (
                                                                <li key={index}>
                                                                    <a href={sm.url} target="_blank"
                                                                       rel="noopener noreferrer"
                                                                       aria-label={sm?.platform?.name}>
                                                                        {sm?.platform?.icon ? ( // Show icon if available
                                                                            <img src={sm.platform.icon}
                                                                                 alt={sm.platform.name || 'Social Media'}/>
                                                                        ) : ( // Otherwise show name
                                                                            sm?.platform?.name
                                                                        )}
                                                                    </a>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </li>
                                            )}
                                        </ul>
                                        {/* Share Profile Button (visible to all, uses prop) */}
                                        {showSharePopup && typeof showSharePopup === 'function' && (
                                            <button onClick={() => showSharePopup({
                                                companyName: name,
                                                telephone: phone_number, // Pass phone
                                                address: formattedAddress, // Pass address
                                                // Pass specializations if available
                                                specializations: currentLegalCompany?.specializations ? currentLegalCompany.specializations.map(spec => spec.name) : []
                                            })} type='button'
                                                    className='profile__share button no-style'>
                                                Share Profile
                                                <img src="/assets/icon--share.svg" alt="Share"/>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {/* Right Column (empty in this block) */}
                                <div className="col-2">
                                    {/* Content for right column was in other blocks */}
                                </div>
                            </div>
                            {/* /.profile__content */}
                        </div>
                        {/* /.container */}
                    </div>
                    {/* /.profile__block (main) */}

                    {/* Service Regions Block */}
                    {(isOwner || (detailed_service_regions?.length || 0) > 0) && (
                        <div className="profile__block">
                            <div className="container">
                                <div className="profile__header">
                                    <h2>Service Regions</h2>
                                    {isOwner && (
                                        <button className="button no-style" onClick={handleShowEditServiceRegionsPopup}>
                                            <img src="/assets/icon--edit-Z9QfuMgm.svg" alt="Edit"/>
                                            {(detailed_service_regions?.length || 0) === 0 ? 'Add Regions' : 'Edit Regions'}
                                        </button>
                                    )}
                                </div>
                                <div className="profile__content">
                                    <ul className="params__list">
                                        {Object.keys(groupedServiceRegions).length > 0 ? (
                                            Object.keys(groupedServiceRegions).map((cityState, index) => (
                                                <li key={cityState || index}>
                                                    <div onClick={() => toggleCityExpansion(cityState)}
                                                         style={{cursor: 'pointer'}}>
                                                        {cityState}
                                                        {typeof ZipCodes === 'function' &&
                                                            <ZipCodes zipCodes={groupedServiceRegions[cityState]}/>}
                                                    </div>
                                                </li>
                                            ))
                                        ) : (
                                            !isOwner ? (
                                                <li>No service regions specified.</li>
                                            ) : (
                                                <li>No regions added. Click 'Add Regions'.</li>
                                            )
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Attorneys Block */}
                    {(isOwner || (attorneys?.length || 0) > 0) && (
                        <div className="profile__block">
                            <div className="container">
                                <div className="profile__header">
                                    <h2>{attorneys?.length || 0} attorney{(attorneys?.length || 0) !== 1 ? 's' : ''} on
                                        staff</h2>
                                    {isOwner && (
                                        <button className="button no-style" onClick={handlerShowAddAttorneysPopup}>
                                            <img src="/assets/icon--edit-Z9QfuMgm.svg" alt="Add"/>
                                            Add Attorney
                                        </button>
                                    )}
                                </div>
                                <div className="profile__content">
                                    <div className="attorneys">
                                        {attorneys?.length > 0 ? (
                                            <>
                                                <ul className="attorneys__list">
                                                    {displayedAttorneys.map((attorney, index) => (
                                                        <li key={attorney.attorney_id || index} className="attorney"
                                                            onClick={() => handleAttorneyClick(attorney)}
                                                            style={{cursor: 'pointer'}}
                                                            title={`View profile of ${attorney.first_name} ${attorney.last_name}`}>
                                                            <div className="attorney__avatar">
                                                                <div className="avatar">
                                                                    <picture>
                                                                        {attorney.photo ? (
                                                                            <>
                                                                                <source type="image/webp"
                                                                                        srcSet={attorney.photo}/>
                                                                                <source type="image/jpg"
                                                                                        srcSet={attorney.photo}/>
                                                                                <img src={attorney.photo}
                                                                                     alt={`${attorney.first_name} ${attorney.last_name}`}/>
                                                                            </>
                                                                        ) : (
                                                                            <div className="avatar-placeholder"></div>
                                                                        )}
                                                                    </picture>
                                                                </div>
                                                            </div>
                                                            <div className="attorney__content">
                                                                <div>
                                                                    <h3 className="attorney__name">{attorney.first_name} {attorney.last_name}</h3>
                                                                    <div className="attorney__info">
                                                                        {attorney.license_year ? `Licensed in ${attorney.license_year}` : ''}
                                                                        {attorney.total_won_amount && (
                                                                            <span> • Total won amount: {formatAmount(attorney.total_won_amount)}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {(attorney.specializations?.length || 0) > 0 && (
                                                                    <ul className="params__list">
                                                                        {attorney.specializations.slice(0, 5).map((spec, specIndex) => (
                                                                            <li key={spec.id || specIndex}>{spec.name}</li>
                                                                        ))}
                                                                        {attorney.specializations.length > 5 && (
                                                                            <li>+ {attorney.specializations.length - 5} more</li>
                                                                        )}
                                                                    </ul>
                                                                )}
                                                            </div>
                                                            {isOwner && (
                                                                <button className="button no-style"
                                                                        title="Delete Attorney"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            console.log("Delete attorney:", attorney.attorney_id);
                                                                            showNotification("Action Not Implemented", "Attorney deletion needs to be implemented.", null, "warning");
                                                                        }}>
                                                                    <img src="/assets/icon--delete-gray-BAOsHS3n.svg"
                                                                         alt="Delete"/>
                                                                </button>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                                {(attorneys?.length || 0) > 2 && (
                                                    <div className="show-all">
                                                        <button className="button-link" onClick={(e) => {
                                                            e.preventDefault();
                                                            setShowAllAttorneys(!showAllAttorneys);
                                                        }}>
                                                            {showAllAttorneys ? 'Show Less' : `Show ${remainingAttorneysCount} More Attorneys`}
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            isOwner && <p>No attorneys added. Click 'Add Attorney'.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Awards and Articles Block */}
                    {(isOwner || (awards?.length || 0) > 0) && (
                        <div className="profile__block">
                            <div className="container">
                                <div className="profile__header">
                                    <h2>Awards and Articles</h2>
                                    {isOwner && (
                                        <button className="button no-style" onClick={handlerShowEditAwardsPopup}>
                                            <img src="/assets/icon--edit-Z9QfuMgm.svg" alt="Edit"/>
                                            {(awards?.length || 0) === 0 ? 'Add Awards' : 'Edit Awards'}
                                        </button>
                                    )}
                                </div>
                                <div className="profile__content">
                                    <div className="col-2 decor-list">
                                        {(awards?.length || 0) > 0 ? (
                                            awards.map((award, index) => (
                                                <div key={award.id || index}
                                                     className="vendor vendor-award decor-list__el">
                                                    <div className="vendor__award">
                                                        {award.image &&
                                                            <img src={award.image} alt={award.title || 'Award'}/>}
                                                    </div>
                                                    <div className="vendor__header">
                                                        <h4 className="vendor__name">{award.title}</h4>
                                                        <span className="vendor__attorney">{award.subtitle}</span>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            !isOwner ? (
                                                <p>No awards listed.</p>
                                            ) : (
                                                <p>No awards added. Click 'Add Awards'.</p>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            )}

            {selectedAttorney && (
                <PopupAttorney
                    visible={!!selectedAttorney}
                    attorney={selectedAttorney}
                    onClose={() => setSelectedAttorney(null)}
                />
            )}

            {isOwner && showEditAwardsPopup && (
                <EditAwardsPopup
                    show={showEditAwardsPopup}
                    onClose={handlerCloseEditAwardsPopup}
                    initialData={currentLegalCompany}
                    onSave={handleSaveAwards}
                />
            )}
            {isOwner && showEditProfilePopup && (
                <EditLegalCompanyProfilePopup
                    show={showEditProfilePopup}
                    closeEditProfilePopup={handlerCloseEditProfilePopup}
                    initialData={currentLegalCompany}
                    onSave={handleSaveProfile}
                />
            )}
            {isOwner && showAddAttorneysPopup && (
                <AddAttorneysPopup
                    show={showAddAttorneysPopup}
                    onClose={handlerCloseAddAttorneysPopup}
                    legalCompanyId={currentLegalCompany?.id}
                    onAttorneyAdded={fetchLegalCompanyData}
                />
            )}
            {isOwner && showEditServiceRegionsPopup && (
                <EditServiceRegionsPopup
                    show={showEditServiceRegionsPopup}
                    onClose={handleCloseEditServiceRegionsPopup}
                    serviceRegions={groupedServiceRegions}
                    legalCompanyId={currentLegalCompany?.id}
                    onSave={handleSaveServiceRegions}
                />
            )}

            {showSendMessageModal && (
                <SendMessageModal
                    show={showSendMessageModal}
                    recipient={currentLegalCompany}
                    recipientType="legalcompany"
                    onClose={handleCloseSendMessageModal}
                />
            )}

            {/* MODIFIED: RequestConsultation Modal (renders if state=true) */}
            {showRequestConsultationModal && ( // MODIFIED
                <RequestConsultationModal // MODIFIED
                    show={showRequestConsultationModal} // MODIFIED
                    recipient={currentLegalCompany}
                    recipientType="legalcompany"
                    onClose={handleCloseRequestConsultationModal} // MODIFIED
                />
            )}

            {isOwner && showCropModal && imageToCrop && (
                <CropImageModal
                    show={showCropModal}
                    onClose={() => {
                        if (imageToCrop) URL.revokeObjectURL(imageToCrop);
                        setShowCropModal(false);
                        setImageToCrop(null);
                        setCurrentFieldToUpdate(null);
                    }}
                    image={imageToCrop}
                    aspect={currentAspectRatio}
                    onComplete={handleCroppedImageComplete}
                />
            )}

            {showClaimProfileModal && (
                <ClaimProfileModal
                    show={showClaimProfileModal}
                    companyToClaim={currentLegalCompany}
                    onClose={handleCloseClaimProfileModal}
                />
            )}

        </div>
    );
};

export default Popup;
